import { I18n } from 'react-redux-i18n';
import omit from 'lodash/omit';

import { FieldSelector } from '@common/components/FieldSelector';
import { Ellipsis } from '@common/components/Ellipsis';
import { Flex } from '@common/components/Flex';
import { getTestAttributes } from '@common/utils/tests';
import { MODULE_NAME } from '../../pedigree-import';
import { COLUMN_TYPES, IMPORT_TYPE } from '../../pedigree-import.constants';
import { Menu, MENU_ITEM_HEIGHT } from './components/menu';
import { getFieldChooserFolders, getFieldChooserFields } from '../../actions/main';

import './file-view.styles.scss';

export const FileView = (props) => {
  const { columns, data, columnsTypeIndex, onColumnsTypeIndexChange, importType, onColumnsFieldIndexChange, columnsFieldIndex } = props;
  const [menuCoordinates, setMenuCoordinates] = React.useState();
  const [currentColumnIndex, setCurrentColumnIndex] = React.useState();
  const [fieldChooserOpened, setFieldChooserOpened] = React.useState(false); 

  const handleMenuClose = React.useCallback(() => {
    setMenuCoordinates(undefined);
  }, []);

  const handleColumnTypeSelect = React.useCallback(({ id }) => {
    const columnsFieldIndexCopy = { ...columnsFieldIndex };
    delete columnsFieldIndexCopy[currentColumnIndex];
    const sameTypeIndex = Object.keys(columnsFieldIndexCopy).find(
        (index) => columnsFieldIndexCopy[index]?.action === id
    );

    if (sameTypeIndex !== undefined) {
      delete columnsFieldIndexCopy[sameTypeIndex];
    }

    onColumnsFieldIndexChange({
      ...columnsFieldIndexCopy,
      [currentColumnIndex]: {
        ...(columnsFieldIndex[currentColumnIndex] || {}),
        action: id
      }
    });

    setMenuCoordinates(undefined);
    setCurrentColumnIndex(undefined);
  }, [columnsFieldIndex, onColumnsFieldIndexChange, currentColumnIndex]);

  const allMenuItems = React.useMemo(() => {
    return {
      [COLUMN_TYPES.pedigreeName]: I18n.t('Pedigree name'),
      [COLUMN_TYPES.individualName]: I18n.t('Individual Name'),
      [COLUMN_TYPES.motherId]: I18n.t('Mother ID'),
      [COLUMN_TYPES.fatherId]: I18n.t('Father ID'),
      [COLUMN_TYPES.sex]: I18n.t('Sex'),
      [COLUMN_TYPES.child]: I18n.t('Child'),
      [COLUMN_TYPES.sibling]: I18n.t('Sibling'),
      [COLUMN_TYPES.spouse1]: I18n.t('Spouse 1'),
      [COLUMN_TYPES.spouse2]: I18n.t('Spouse 2'),
      [COLUMN_TYPES.twinStatus]: I18n.t('Twin Status'),
      [COLUMN_TYPES.twinRelationship]: I18n.t('Twin Relationship'),
      [COLUMN_TYPES.probandStatus]: I18n.t('Proband Status'),
      [COLUMN_TYPES.deceased]: I18n.t('Deceased'),
    };
  }, []);

  const individualMenuItems = React.useMemo(() => {
    return {
      [COLUMN_TYPES.individualName]: I18n.t('Individual Name'),
      [COLUMN_TYPES.sex]: I18n.t('Sex'),
      [COLUMN_TYPES.deceased]: I18n.t('Deceased'),
    };
  }, []);

  const menuItems = importType === IMPORT_TYPE.individuals ? individualMenuItems : allMenuItems;

  const handleContextMenu = React.useCallback((e) => {
    setMenuCoordinates({
      x: e.clientX,
      y: e.clientY - Object.keys(menuItems).length * 0.75 * MENU_ITEM_HEIGHT,
    });
    setCurrentColumnIndex(Number(e.target.dataset.index));
  }, [menuItems]);

  const openFieldChooser = React.useCallback(() => setFieldChooserOpened(true), []);
  const closeFieldSelector = React.useCallback(() => setFieldChooserOpened(false), []);

  const handleGetFields = React.useCallback(({ folderId }) => {
    return getFieldChooserFields({ context: IMPORT_TYPE.individuals, folderId });
  }, [importType]);

  const handleGetFolders = React.useCallback(() => {
    return getFieldChooserFolders({ context: IMPORT_TYPE.individuals });
  }, [importType]);

  const handleFieldChange = React.useCallback(({ field }) => {
    const columnsTypeIndexCopy = { ...columnsTypeIndex };
    delete columnsTypeIndexCopy[currentColumnIndex];

    let columnsFieldIndexCopy = { ...columnsFieldIndex };

    const sameTypeIndex = Object.keys(columnsFieldIndexCopy).find((index) => columnsFieldIndexCopy[index]?.id === field.fieldId);

    if (sameTypeIndex !== undefined) {
      columnsFieldIndexCopy = omit(columnsFieldIndexCopy, [sameTypeIndex]);
    }

    onColumnsTypeIndexChange(columnsTypeIndexCopy);
    onColumnsFieldIndexChange({ ...columnsFieldIndexCopy, [currentColumnIndex]: { id: field.fieldId, name: field.fieldName, imageURL: field.imageURL } });
    setCurrentColumnIndex(undefined);
    setMenuCoordinates(undefined);
  }, [onColumnsTypeIndexChange, onColumnsFieldIndexChange, currentColumnIndex, columnsTypeIndex, columnsFieldIndex]);

  const cellWidth = 95;
  const widthSnippet = {
    width: cellWidth,
    minWidth: cellWidth,
  };
  const borderWidth = 1;

  return (
    <div className="PedigreeFileView">
      {fieldChooserOpened && (
        <FieldSelector 
          getFolders={handleGetFolders}
          getFields={handleGetFields}
          onChange={handleFieldChange}
          close={closeFieldSelector}
        />
      )}
      <h6>
        {I18n.t('Right-click on headers to specify columns')}
      </h6>

      <div className="PedigreeFileView__table" style={{ width: columns?.length ? (columns.length * cellWidth) + borderWidth * 2 : '100%' }}>
        {menuCoordinates && (
          <Menu 
            items={Object.keys(menuItems).map((x) => ({ id: x, name: menuItems[x] }))}
            {...menuCoordinates}
            onTypeSelect={handleColumnTypeSelect}
            onChooseExistingField={openFieldChooser}
            onClose={handleMenuClose}
          />
        )}

        {columns && data && (
          <>
            <Flex inline width="auto" className="PedigreeFileView__table__trigger-row">
              {columns.map((x, i) => {
                const columnData = columnsFieldIndex[i];
                const text = allMenuItems[columnData?.action] || columnData?.name;

                return (
                  <span
                    key={x}
                    data-index={i}
                    title={text}
                    className="PedigreeFileView__table__trigger-row__cell" 
                    style={widthSnippet}
                    {...getTestAttributes(MODULE_NAME, 'file-view-trigger-header', 'cell', i)}
                    onContextMenu={handleContextMenu}
                  >
                    {columnsFieldIndex[i]?.imageURL && <img src={columnsFieldIndex[i]?.imageURL} />}{' '}
                    {text}
                  </span>
                );
              })}
            </Flex>
            <Flex inline width="auto" className="PedigreeFileView__table__header-row">
              {columns.map((x, i) => (
                <span 
                  key={x} 
                  {...getTestAttributes(MODULE_NAME, 'file-view-header', 'cell', i)} 
                  title={x} 
                  className="PedigreeFileView__table__header-row__cell"
                  style={widthSnippet}
                >
                  {x}
                </span>
              ))}
            </Flex>
            <div className="PedigreeFileView__table__body">
              {data.map((x, i) => (
                <Flex key={i} inline width="auto" className="PedigreeFileView__table__body__row" {...getTestAttributes(MODULE_NAME, 'file-view-body', 'row', i)}>
                  {columns.map((columnField, j) => (
                    <Ellipsis
                      key={columnField}
                      {...getTestAttributes(MODULE_NAME, 'file-view-body', 'cell', j)}
                      className="PedigreeFileView__table__body__row__cell"
                      style={widthSnippet}
                      text={x[columnField]}
                    />
                  ))}
                </Flex>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};